import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { LinkCTA } from 'Src/components/buttons/LinkCTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { useDOMEventListener } from 'Src/feature/dom-event/useDOMEventListener';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { TextModal } from 'Src/feature/modal/TextModal';
import { useModalControl } from 'Src/feature/modal/useModalControl';

import TerminalClaimedDialog from './TerminalClaimedDialog';
import { useProvisionerTerminal } from './useProvisionerTerminal';
import { useTokenWorkflow } from './useTokenWorkflow';
import WaitSplash from './WaitSplash';

import styles from './EnterToken.module.scss';

interface FormModel {
  token: string;
}
const { Form, Field } = getTypedFormComponents<FormModel>();

const sectionId = 'EnterToken';

const EnterToken: FC = () => {
  const { t } = useTranslation();

  const tokenRef = useRef(undefined as undefined | HTMLInputElement);
  const workflow = useTokenWorkflow();
  const [code, codeSet] = useState(undefined as string | undefined);
  const terminal = useProvisionerTerminal.query.getTerminal({ code: code as string });

  const modal = {
    terminalClaimed: useModalControl(),
    whereFindCode: useModalControl(),
  };

  const form = useForm<FormModel>({
    defaultValues: { token: '' },
    schema: object().shape({
      token: string().label(t('labels.token')).length(12).required(),
    }),
  });

  const onAfterTokenChange = (inputValue: string) => {
    // UPPERCASE & remove non-alpha/numeric characters
    const nextInputValue = (inputValue || '').replace(/\W+/g, '').toUpperCase();
    // format/store as an uppercase value in form store
    form.action.setValue('token', nextInputValue);
    workflow.updateData({
      terminal: undefined,
      token: nextInputValue,
    });
    workflow.steps.canGoNext = nextInputValue.length === 12;
  };

  const onKillClaimCancelled = () => {
    modal.terminalClaimed.close();
    workflow.updateData({
      terminal: undefined,
      location: undefined,
      token: '',
    });
    workflow.steps.canGoNext = false;
    // trigger focus to the "token" input after the modal closes
    setTimeout(() => tokenRef.current?.focus(), 0);
  };

  const onClaimKilled = () => {
    if(workflow.data.terminal) {
      workflow.updateData({
        terminal: {
          ...workflow.data.terminal,
          isProvisioned: false,
        }
      });
      workflow.steps.goNext();
    } else {
      console.warn(sectionId, 'onClaimKilled', '!workflow.currentTerminal');
    }
  };

  const onEnter = useCallback(async () => {
    await form.action.submitIfValid(async () => {
      workflow.steps.goNext();
    });
  }, [form.action, workflow.steps]);

  // hardware keyboard "enter" key handler
  useDOMEventListener('keydown', useCallback((evt) => {
    if (/enter/i.test(evt.code)) void onEnter();
  }, [onEnter]));

  useDOMEventListener('tokenWorkflow.onGoNext', useCallback((evt) => {
    if (workflow.data.terminal)  return;
    evt.preventDefault();
    codeSet(form.action.getValues().token);
  }, [form.action, workflow.data.terminal]));

  // only initialize upon component's first mount
  useEffect(() => {
    workflow.steps.canGoNext = (form.action.getValues().token || '').length === 12;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (terminal.isLoading) return;
    if (!terminal.data) return;
    workflow.updateData({
      terminal: terminal.data.terminal,
      location: terminal.data.location,
    });
    if (terminal.data.terminal.isProvisioned) {
      modal.terminalClaimed.open();
      return;
    }
    workflow.steps.goNext();
    // we only want to react when terminal data is loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminal.isLoading, terminal.data]);

  return (
    <Flex alignItems="center" justifyContent="center" gap="sm" fullHeight fullWidth>
      <Copy className={styles['input-title']} variant="h6" color="textSecondary" value={t('provision.tokenWorkflow.instructions')} />
      <Form
        className={styles['form']}
        usedForm={form}
        omitChangeTracking
      >
        <Field
          inputRef={tokenRef}
          type="text"
          name="token"
          autoComplete="off"
          fullWidth
          // hide the required "*" character
          // - added to the styles['input-title'] copy
          required={false}
          classes={{ input: styles['input'] }}
          onAfterChange={({ value }) => onAfterTokenChange(value)}
        />
        <span />
      </Form>
      <Copy color="textSecondary" value={t('provision.tokenWorkflow.autoPairInfo')} />
      <LinkCTA underline onClick={modal.whereFindCode.open}>
        {t('provision.tokenWorkflow.whereFindCode')}
      </LinkCTA>
      <TerminalClaimedDialog
        open={modal.terminalClaimed.isOpen}
        onCancel={onKillClaimCancelled}
        onClaimKilled={onClaimKilled}
        terminal={workflow.data.terminal}
      />
      {terminal.isLoading && <WaitSplash message={t('provision.labels.loadingTerminalSettings')} />}
      <TextModal
        open={modal.whereFindCode.isOpen}
        title={t('provision.tokenWorkflow.whereFindCode')}
        textContent={t('provision.tokenWorkflow.whereFindCodeDetail')}
        onBackDropClick={modal.whereFindCode.close}
        actions={(
          <ModalAction
            color="secondary"
            label={t('labels.okay')}
            onClick={modal.whereFindCode.close}
          />
        )}
      />
    </Flex>
  );
};

export { EnterToken };
